// LT-359 (LT-326) — Contact List detail page.
//
// Route: /contact-lists/:listId
//
// Surfaces the list's metadata + (for static lists) member-management
// table with add (multi-select picker) + remove. Dynamic lists show a
// read-only members preview from `evaluate_dynamic` + a button to
// snapshot the membership for ad-hoc campaign-launch use.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, Plus, Search, Trash2 } from 'lucide-react';

import PageLoader from '../../components/PageLoader';
import PageShell from '../../components/PageShell';
import { contactListsAPI } from '../../services/contactListsAPI';
import { contactsAPI } from '../../services/contactsAPI';
import type { Contact } from '../../types/contact';
import {
  ContactList,
  ContactListEvaluateResponse,
} from '../../types/contactList';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
  type ColumnDef,
} from '../../ui';

interface DisplayMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  lifecycle_stage: string;
}

function toDisplayMember(c: Contact): DisplayMember {
  const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return {
    id: c.id,
    name: fullName || c.email || c.phone || 'Unnamed contact',
    email: c.email ?? '',
    phone: c.phone ?? '',
    lifecycle_stage: c.lifecycle_stage,
  };
}

const ContactListDetailPage: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();

  const [list, setList] = useState<ContactList | null>(null);
  const [members, setMembers] = useState<ContactListEvaluateResponse | null>(null);
  const [memberContacts, setMemberContacts] = useState<DisplayMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerResults, setPickerResults] = useState<Contact[]>([]);
  const [pickerSelected, setPickerSelected] = useState<Set<string>>(new Set());
  const [pickerLoading, setPickerLoading] = useState(false);

  const [snapshotMsg, setSnapshotMsg] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!listId) return;
    setLoading(true);
    setErr(null);
    try {
      const [cl, evalResp] = await Promise.all([
        contactListsAPI.get(listId),
        contactListsAPI.evaluate(listId),
      ]);
      setList(cl);
      setMembers(evalResp);

      // Fetch each member contact individually — there's no batched
      // GET /contacts?ids=... endpoint today. For typical list sizes
      // (≤500) this is acceptable; if growth pressure surfaces a
      // batched endpoint can land in a follow-up.
      if (evalResp.contact_ids.length === 0) {
        setMemberContacts([]);
      } else {
        const fetched = await Promise.allSettled(
          evalResp.contact_ids.map((id) => contactsAPI.get(id)),
        );
        const contacts = fetched
          .filter((r): r is PromiseFulfilledResult<Contact> => r.status === 'fulfilled')
          .map((r) => r.value)
          .map(toDisplayMember);
        setMemberContacts(contacts);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load contact list');
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleRemoveMember = useCallback(
    async (contactId: string) => {
      if (!list) return;
      if (!window.confirm('Remove this contact from the list?')) return;
      try {
        await contactListsAPI.removeMembers(list.id, { contact_ids: [contactId] });
        await reload();
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Failed to remove member');
      }
    },
    [list, reload],
  );

  const runPickerSearch = useCallback(async () => {
    setPickerLoading(true);
    try {
      const rows = await contactsAPI.list({
        q: pickerQuery.trim() || undefined,
        limit: 50,
      });
      setPickerResults(rows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to search contacts');
    } finally {
      setPickerLoading(false);
    }
  }, [pickerQuery]);

  useEffect(() => {
    if (showPicker) {
      void runPickerSearch();
    }
  }, [showPicker, runPickerSearch]);

  const handleAddSelected = useCallback(async () => {
    if (!list || pickerSelected.size === 0) return;
    try {
      await contactListsAPI.addMembers(list.id, {
        contact_ids: Array.from(pickerSelected),
      });
      setShowPicker(false);
      setPickerSelected(new Set());
      setPickerQuery('');
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to add members');
    }
  }, [list, pickerSelected, reload]);

  const handleSnapshot = useCallback(async () => {
    if (!list) return;
    try {
      const snap = await contactListsAPI.snapshot(list.id, {});
      setSnapshotMsg(
        `Snapshot taken (${snap.member_count} members) — id ${snap.id.slice(0, 8)}…`,
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to take snapshot');
    }
  }, [list]);

  const memberColumns = useMemo<ColumnDef<DisplayMember>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: (m) => <span className="font-medium text-slate-50">{m.name}</span>,
      },
      {
        id: 'email',
        header: 'Email',
        accessor: (m) => <span className="text-slate-300">{m.email || '—'}</span>,
      },
      {
        id: 'phone',
        header: 'Phone',
        accessor: (m) => (
          <span className="font-mono text-xs text-slate-400">{m.phone || '—'}</span>
        ),
      },
      {
        id: 'lifecycle_stage',
        header: 'Stage',
        accessor: (m) => <span className="text-slate-300">{m.lifecycle_stage}</span>,
      },
    ],
    [],
  );

  const isStatic = list?.list_type === 'static';

  const memberRowActions = useCallback(
    (m: DisplayMember) =>
      isStatic ? (
        <Button
          intent="ghost"
          aria-label={`Remove ${m.name}`}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            void handleRemoveMember(m.id);
          }}
        >
          <Trash2 className="w-4 h-4" strokeWidth={2} />
        </Button>
      ) : null,
    [isStatic, handleRemoveMember],
  );

  if (loading) return <PageLoader />;
  if (!list) {
    return (
      <PageShell>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <p className="text-danger-text">{err ?? 'Contact list not found.'}</p>
          <Link to="/contact-lists" className="text-accent text-sm mt-4 inline-block">
            ← Back to contact lists
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link
          to="/contact-lists"
          className="text-slate-400 text-sm hover:text-slate-200 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back to contact lists
        </Link>

        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs font-mono uppercase tracking-wide text-slate-400 mb-2">
              {list.list_type.toUpperCase()} LIST
            </p>
            <h1 className="text-3xl font-semibold text-slate-50">{list.name}</h1>
            {list.description && (
              <p className="text-slate-300 mt-2 max-w-2xl">{list.description}</p>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button intent="secondary" onClick={() => void handleSnapshot()}>
              <Camera className="w-4 h-4" strokeWidth={2} />
              Take snapshot
            </Button>
            {snapshotMsg && (
              <p className="text-xs text-ok-text">{snapshotMsg}</p>
            )}
          </div>
        </div>

        {err && (
          <Card className="mb-4 border-danger">
            <p className="text-danger-text">{err}</p>
          </Card>
        )}

        {!isStatic && list.filter_dsl && (
          <Card className="mb-6">
            <h3 className="text-sm font-semibold text-slate-200 mb-2">Saved filter</h3>
            <pre className="text-xs font-mono text-slate-300 whitespace-pre-wrap break-all">
              {JSON.stringify(list.filter_dsl, null, 2)}
            </pre>
            <p className="text-xs text-slate-400 mt-2">
              Members below are evaluated live. Take a snapshot to lock the membership for a campaign launch.
            </p>
          </Card>
        )}

        {isStatic && (
          <div className="flex justify-end mb-3">
            <Button
              intent="primary"
              onClick={() => {
                setShowPicker(true);
                setPickerSelected(new Set());
                setPickerQuery('');
              }}
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              Add members
            </Button>
          </div>
        )}

        {showPicker && (
          <Card className="mb-4">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-200">Add contacts</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name or email…"
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void runPickerSearch();
                  }}
                />
                <Button intent="secondary" onClick={() => void runPickerSearch()}>
                  <Search className="w-4 h-4" strokeWidth={2} />
                  Search
                </Button>
              </div>
              {pickerLoading ? (
                <p className="text-slate-400 text-sm">Searching…</p>
              ) : (
                <div
                  className="max-h-80 overflow-auto border border-white/[0.08] rounded-ds-md"
                  role="listbox"
                  aria-multiselectable="true"
                  aria-label="Contact picker results"
                >
                  {pickerResults.length === 0 ? (
                    <p className="text-slate-400 text-sm p-3">No contacts match.</p>
                  ) : (
                    pickerResults.map((c) => {
                      const checked = pickerSelected.has(c.id);
                      const display = toDisplayMember(c);
                      return (
                        <label
                          key={c.id}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.02] cursor-pointer"
                          role="option"
                          aria-selected={checked}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = new Set(pickerSelected);
                              if (e.target.checked) next.add(c.id);
                              else next.delete(c.id);
                              setPickerSelected(next);
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-100">{display.name}</p>
                            <p className="text-xs text-slate-400 font-mono truncate">
                              {display.email || display.phone || '—'}
                            </p>
                          </div>
                          <span className="text-xs text-slate-400">{display.lifecycle_stage}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  intent="primary"
                  disabled={pickerSelected.size === 0}
                  onClick={() => void handleAddSelected()}
                >
                  Add {pickerSelected.size > 0 ? `(${pickerSelected.size})` : ''}
                </Button>
                <Button intent="secondary" onClick={() => setShowPicker(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {memberContacts.length === 0 ? (
          <EmptyState
            surface="contact_list_members"
            icon={Plus}
            headline={isStatic ? 'No members yet' : 'No matches'}
            subhead={
              isStatic
                ? "Add contacts to this list using the 'Add members' button above."
                : "The saved filter currently matches zero contacts in this workspace."
            }
            primary={{
              label: isStatic ? 'Add members' : 'Edit filter',
              icon: isStatic ? Plus : Search,
              onClick: () => {
                if (isStatic) {
                  setShowPicker(true);
                } else {
                  navigate('/contact-lists');
                }
              },
            }}
          />
        ) : (
          <Card padding="tight">
            <div className="px-4 py-3 border-b border-white/[0.08]">
              <h2 className="text-sm font-semibold text-slate-200">
                Members{' '}
                <span className="font-mono text-xs text-slate-400">
                  ({members?.member_count ?? memberContacts.length})
                </span>
              </h2>
            </div>
            <DataTable<DisplayMember>
              data={memberContacts}
              columns={memberColumns}
              getRowKey={(row) => row.id}
              onRowClick={(row) => navigate(`/contacts/${row.id}`)}
              renderRowActions={memberRowActions}
              ariaLabel="Contact list members"
            />
          </Card>
        )}
      </div>
    </PageShell>
  );
};

export default ContactListDetailPage;
